import { Renamed } from 'jobs/creation/DraftChange'
import { DraftStateSlice, PrepareContext } from 'jobs/creation/DraftStateSlice'
import { StepFactory, StepViewModel } from 'jobs/creation/OutlineViewModel'
import { action, computed, observable, makeObservable } from 'mobx'
import { Observable, Subject } from 'rxjs'

const TITLE = 'Do you want to give this job a name?'

export default class NameInputViewModel extends DraftStateSlice {
  /**
   * The singleton step for this slice.
   */
  readonly step: StepViewModel

  /**
   * Used to emit changes so we know when to trigger a save.
   *
   * @private
   */
  private changesSubject = new Subject<Renamed>()

  /**
   * Emits a value when the name was changed by the user.
   */
  changes$ = this.changesSubject.asObservable()

  /**
   * Job name.
   */
  @observable name: string = ''

  /**
   * Constructor.
   *
   * @param isActive$ Observable for whether the slice is active.
   * @param createStep Factory for creating a Step.
   * @param _isDisabled Getter for whether the selector is disabled
   * @param _canEditDraftJobsAsAdmin Getter for whether the selector is visible
   */
  constructor(
    isActive$: Observable<boolean>,
    createStep: StepFactory,
    private _isDisabled: () => boolean,
    private _canEditDraftJobsAsAdmin: () => boolean
  ) {
    super(isActive$)
    makeObservable(this)

    this.step = createStep({
      stepId: 'name',
      // This is an optional field for all scenarios, if not specified, name will come
      // from legend calculation.
      isValid: () => true,
      outlineItemDisplay: () => {
        return {
          title: TITLE,
          value: this.name,
          visual: {
            type: 'QuestionIcon',
            questionType: 'TEXT',
          },
        }
      },
    })
  }

  /**
   * This is the only step container.
   */
  @computed
  get stepContainers() {
    return this._canEditDraftJobsAsAdmin() ? [this] : []
  }

  /**
   * Whether this control is readonly.
   */
  @computed
  get disabled() {
    return this._isDisabled()
  }

  /**
   * Activates the VM and hydrates state from a Job.
   */
  async prepare(context: PrepareContext) {
    if (context.type === 'EditDraft') {
      this.setName(context.job.name)
    }
  }

  /**
   * Sets the name.
   *
   * @param name
   */
  @action.bound
  setName(newName: string) {
    this.name = newName
  }

  /**
   * Renames the job.
   *
   * @param newName
   */
  @action.bound
  rename(newName: string) {
    this.name = newName.trim()
    this.changesSubject.next({ newName, type: 'Renamed' })
  }
}
